import { oryService } from '../ory/ory-service';
export var PasskeyErrorCode;
(function (PasskeyErrorCode) {
    PasskeyErrorCode["NOT_SUPPORTED"] = "NOT_SUPPORTED";
    PasskeyErrorCode["REGISTRATION_FAILED"] = "REGISTRATION_FAILED";
    PasskeyErrorCode["AUTHENTICATION_FAILED"] = "AUTHENTICATION_FAILED";
    PasskeyErrorCode["USER_CANCELLED"] = "USER_CANCELLED";
    PasskeyErrorCode["TIMEOUT"] = "TIMEOUT";
    PasskeyErrorCode["INVALID_STATE"] = "INVALID_STATE";
    PasskeyErrorCode["SECURITY_ERROR"] = "SECURITY_ERROR";
    PasskeyErrorCode["NETWORK_ERROR"] = "NETWORK_ERROR";
    PasskeyErrorCode["NOT_AVAILABLE"] = "NOT_AVAILABLE";
    PasskeyErrorCode["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
    PasskeyErrorCode["SESSION_EXPIRED"] = "SESSION_EXPIRED";
})(PasskeyErrorCode || (PasskeyErrorCode = {}));
/**
 * Passkey Service
 *
 * Handles passkey (platform authenticator) registration and authentication
 * using Ory Kratos v1.2.0+ dedicated passkey strategy
 */
export class PasskeyService {
    static instance;
    constructor() { }
    static getInstance() {
        if (!PasskeyService.instance) {
            PasskeyService.instance = new PasskeyService();
        }
        return PasskeyService.instance;
    }
    /**
     * Check if passkeys are supported by the browser
     */
    isSupported() {
        return (window.PublicKeyCredential !== undefined &&
            navigator.credentials !== undefined);
    }
    /**
     * Check available passkey capabilities
     */
    async checkCapabilities() {
        if (!this.isSupported()) {
            return {
                supported: false,
                platformAuthenticator: false,
                conditionalMediation: false
            };
        }
        try {
            const platformAuthenticator = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            // Check for conditional mediation support (autofill UI)
            // Note: This is a newer API that may not be available in all browsers
            const conditionalMediation = typeof PublicKeyCredential.isConditionalMediationAvailable === 'function'
                ? await PublicKeyCredential.isConditionalMediationAvailable()
                : false;
            return {
                supported: true,
                platformAuthenticator,
                conditionalMediation
            };
        }
        catch (error) {
            this.logError('Error checking passkey capabilities', error);
            return {
                supported: false,
                platformAuthenticator: false,
                conditionalMediation: false
            };
        }
    }
    /**
     * Register a passkey
     */
    async register(options) {
        if (!this.isSupported()) {
            return {
                success: false,
                error: 'Passkeys are not supported by this browser',
                errorCode: PasskeyErrorCode.NOT_SUPPORTED
            };
        }
        try {
            // Step 1: Get or create settings flow
            const settingsFlow = await oryService.createSettingsFlow();
            // Step 2: Check if WebAuthn options are already available
            let createDataNode = settingsFlow.ui.nodes.find(node => node.group === 'passkey' &&
                node.attributes?.name === 'passkey_create_data');
            let flowWithOptions = settingsFlow;
            // If create_data is not available or empty, we need to trigger registration
            if (!createDataNode || !createDataNode.attributes.value) {
                // Find and trigger passkey registration
                const triggerNode = settingsFlow.ui.nodes.find(node => node.group === 'passkey' &&
                    node.attributes?.name === 'passkey_register_trigger');
                if (!triggerNode) {
                    return {
                        success: false,
                        error: 'Passkey registration not available',
                        errorCode: PasskeyErrorCode.NOT_AVAILABLE
                    };
                }
                // Submit trigger to initiate registration
                const triggerValue = triggerNode.attributes?.value;
                // Create the update body - the field name should match what's in the node
                const fieldName = triggerNode.attributes.name;
                const triggerBody = {
                    method: 'passkey',
                    csrf_token: this.getCSRFToken(settingsFlow)
                };
                triggerBody[fieldName] = triggerValue || '';
                flowWithOptions = await oryService.updateSettingsFlow(settingsFlow.id, triggerBody);
                // Ensure we got a SettingsFlow back
                if (!('ui' in flowWithOptions)) {
                    return {
                        success: false,
                        error: 'Unexpected response from settings update',
                        errorCode: PasskeyErrorCode.REGISTRATION_FAILED
                    };
                }
                // Get the create_data node from the updated flow
                createDataNode = flowWithOptions.ui.nodes.find(node => node.group === 'passkey' &&
                    node.attributes?.name === 'passkey_create_data');
            }
            // Step 3: Extract WebAuthn options
            if (!createDataNode || !createDataNode.attributes.value) {
                return {
                    success: false,
                    error: 'Failed to get passkey creation options',
                    errorCode: PasskeyErrorCode.REGISTRATION_FAILED
                };
            }
            const creationOptions = JSON.parse(createDataNode.attributes.value);
            // Add display name to user
            if (creationOptions.publicKey?.user) {
                creationOptions.publicKey.user.displayName = options.displayName;
            }
            // Configure authenticator selection for platform authenticators
            creationOptions.publicKey.authenticatorSelection = {
                ...creationOptions.publicKey.authenticatorSelection,
                authenticatorAttachment: 'platform',
                userVerification: 'preferred'
            };
            // Set timeout if provided
            if (options.timeout) {
                creationOptions.publicKey.timeout = options.timeout;
            }
            // Convert base64 strings to ArrayBuffers if needed
            if (creationOptions.publicKey.user?.id) {
                creationOptions.publicKey.user.id = this.base64ToArrayBuffer(creationOptions.publicKey.user.id);
            }
            if (creationOptions.publicKey.challenge) {
                creationOptions.publicKey.challenge = this.base64ToArrayBuffer(creationOptions.publicKey.challenge);
            }
            // Step 4: Create credential using browser API
            const credential = await navigator.credentials.create(creationOptions);
            if (!credential || credential.type !== 'public-key') {
                return {
                    success: false,
                    error: 'Failed to create passkey',
                    errorCode: PasskeyErrorCode.REGISTRATION_FAILED
                };
            }
            // Step 5: Submit credential back to Ory
            // Find the correct field name for submission
            const submitNode = flowWithOptions.ui.nodes.find(node => node.group === 'passkey' &&
                node.type === 'input' &&
                node.attributes?.name?.includes('register') &&
                !node.attributes?.name?.includes('trigger'));
            const submitFieldName = submitNode
                ? submitNode.attributes.name
                : 'passkey_settings_register'; // fallback
            // Prepare credential response for submission
            const credentialResponse = {
                id: credential.id,
                rawId: this.arrayBufferToBase64(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: this.arrayBufferToBase64(credential.response.clientDataJSON),
                    attestationObject: this.arrayBufferToBase64(credential.response.attestationObject)
                }
            };
            const submitBody = {
                method: 'passkey',
                csrf_token: this.getCSRFToken(flowWithOptions)
            };
            submitBody[submitFieldName] = JSON.stringify(credentialResponse);
            await oryService.updateSettingsFlow(settingsFlow.id, submitBody);
            return {
                success: true,
                credential: {
                    credentialId: credential.id,
                    type: 'passkey',
                    name: options.displayName
                }
            };
        }
        catch (error) {
            this.logError('Passkey registration error', error);
            // Check for session refresh required error
            if (error.response?.status === 403 && error.response?.data?.error?.id === 'session_refresh_required') {
                return {
                    success: false,
                    error: 'Your session has expired. You can add authentication methods later from your account settings.',
                    errorCode: PasskeyErrorCode.SESSION_EXPIRED
                };
            }
            const { message, code } = this.getErrorDetails(error);
            return {
                success: false,
                error: message,
                errorCode: code
            };
        }
    }
    /**
     * Authenticate using passkey
     */
    async authenticate(loginFlow, options) {
        if (!this.isSupported()) {
            return {
                success: false,
                error: 'Passkeys are not supported by this browser',
                errorCode: PasskeyErrorCode.NOT_SUPPORTED
            };
        }
        try {
            // Use provided flow or create new one
            const flow = loginFlow || await oryService.createLoginFlow();
            // Find passkey authentication node
            const passkeyNode = flow.ui.nodes.find(node => node.group === 'passkey' &&
                node.attributes.node_type === 'script');
            if (!passkeyNode) {
                return {
                    success: false,
                    error: 'Passkey authentication not available',
                    errorCode: PasskeyErrorCode.NOT_AVAILABLE
                };
            }
            // Execute passkey authentication script
            const scriptAttributes = passkeyNode.attributes;
            const authOptions = JSON.parse(scriptAttributes.src);
            // Configure options
            if (options?.timeout) {
                authOptions.publicKey.timeout = options.timeout;
            }
            // Always use 'preferred' for userVerification
            authOptions.publicKey.userVerification = 'preferred';
            // Get credential
            const credential = await navigator.credentials.get(authOptions);
            if (!credential || credential.type !== 'public-key') {
                return {
                    success: false,
                    error: 'Failed to authenticate with passkey',
                    errorCode: PasskeyErrorCode.AUTHENTICATION_FAILED
                };
            }
            // Submit to Ory
            const updateBody = {
                method: 'passkey',
                identifier: '', // Passkeys don't need identifier
                passkey_login: JSON.stringify(credential),
                csrf_token: this.getCSRFToken(flow),
                ...(options?.rememberMe && { remember: true })
            };
            const result = await oryService.updateLoginFlow(flow.id, updateBody);
            // Check if login was successful
            if ('session' in result) {
                return { success: true };
            }
            else {
                return {
                    success: false,
                    error: 'Authentication failed',
                    errorCode: PasskeyErrorCode.AUTHENTICATION_FAILED
                };
            }
        }
        catch (error) {
            this.logError('Passkey authentication error', error);
            const { message, code } = this.getErrorDetails(error);
            return {
                success: false,
                error: message,
                errorCode: code
            };
        }
    }
    /**
     * Authenticate using passkey with conditional mediation (autofill UI)
     * This enables passkey selection directly from the email input field dropdown
     *
     * @param loginFlow - Optional login flow (will create one if not provided)
     * @returns Promise with authentication response
     */
    async authenticateWithConditionalUI(loginFlow) {
        if (!this.isSupported()) {
            return {
                success: false,
                error: 'Passkeys are not supported by this browser',
                errorCode: PasskeyErrorCode.NOT_SUPPORTED
            };
        }
        try {
            // Check if conditional mediation is available
            const capabilities = await this.checkCapabilities();
            if (!capabilities.conditionalMediation) {
                return {
                    success: false,
                    error: 'Conditional mediation not supported',
                    errorCode: PasskeyErrorCode.NOT_AVAILABLE
                };
            }
            // Use provided flow or create new one
            const flow = loginFlow || await oryService.createLoginFlow();
            // Find passkey authentication node
            const passkeyNode = flow.ui.nodes.find(node => node.group === 'passkey' &&
                node.attributes.node_type === 'script');
            if (!passkeyNode) {
                return {
                    success: false,
                    error: 'Passkey authentication not available',
                    errorCode: PasskeyErrorCode.NOT_AVAILABLE
                };
            }
            // Execute passkey authentication script
            const scriptAttributes = passkeyNode.attributes;
            const authOptions = JSON.parse(scriptAttributes.src);
            // Enable conditional mediation - this is the key difference
            authOptions.mediation = 'conditional';
            authOptions.publicKey.userVerification = 'preferred';
            // Get credential with conditional UI
            // This will make the passkey appear in the email input field dropdown
            const credential = await navigator.credentials.get(authOptions);
            if (!credential || credential.type !== 'public-key') {
                return {
                    success: false,
                    error: 'Failed to authenticate with passkey',
                    errorCode: PasskeyErrorCode.AUTHENTICATION_FAILED
                };
            }
            // Submit to Ory
            const updateBody = {
                method: 'passkey',
                identifier: '', // Passkeys don't need identifier
                passkey_login: JSON.stringify(credential),
                csrf_token: this.getCSRFToken(flow)
            };
            const result = await oryService.updateLoginFlow(flow.id, updateBody);
            // Check if login was successful
            if ('session' in result) {
                return { success: true };
            }
            else {
                return {
                    success: false,
                    error: 'Authentication failed',
                    errorCode: PasskeyErrorCode.AUTHENTICATION_FAILED
                };
            }
        }
        catch (error) {
            // Don't log AbortError - user may have just dismissed the prompt
            if (error.name === 'AbortError') {
                return {
                    success: false,
                    error: 'Authentication cancelled',
                    errorCode: PasskeyErrorCode.USER_CANCELLED
                };
            }
            // Don't log as error if conditional mediation is just waiting
            if (error.name === 'NotAllowedError') {
                return {
                    success: false,
                    error: 'Waiting for user interaction',
                    errorCode: PasskeyErrorCode.USER_CANCELLED
                };
            }
            this.logError('Conditional mediation error', error);
            const { message, code } = this.getErrorDetails(error);
            return {
                success: false,
                error: message,
                errorCode: code
            };
        }
    }
    /**
     * Get user's registered passkeys
     */
    async getUserPasskeys() {
        try {
            const session = await oryService.getCurrentSession();
            if (!session?.identity) {
                return [];
            }
            const identity = await oryService.getIdentity(session.identity.id);
            // Extract passkey credentials
            const passkeyCreds = identity.credentials?.passkey;
            if (!passkeyCreds || !passkeyCreds.identifiers) {
                return [];
            }
            // Map to PasskeyCredential format
            return passkeyCreds.identifiers.map((id, index) => ({
                credentialId: id,
                name: `Passkey ${index + 1}`,
                type: 'passkey',
                createdAt: new Date(passkeyCreds.created_at || Date.now())
            }));
        }
        catch (error) {
            this.logError('Failed to get user passkeys', error);
            return [];
        }
    }
    /**
     * Get CSRF token from flow
     */
    getCSRFToken(flow) {
        const csrfNode = flow.ui.nodes.find((node) => node.attributes.node_type === 'input' &&
            node.attributes.name === 'csrf_token');
        return csrfNode ? csrfNode.attributes.value : '';
    }
    /**
     * Convert base64/base64url string to ArrayBuffer
     */
    base64ToArrayBuffer(base64) {
        // Convert base64url to base64
        const base64String = base64
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        // Add padding if needed
        const paddedBase64 = base64String + '=='.substring(0, (4 - base64String.length % 4) % 4);
        const binaryString = atob(paddedBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
    /**
     * Convert ArrayBuffer to base64url string
     */
    arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        // Convert to base64 then to base64url
        return btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }
    /**
     * Get user-friendly error message and code
     */
    getErrorDetails(error) {
        // Handle specific WebAuthn errors
        if (error.name === 'NotAllowedError') {
            return {
                message: 'The operation was cancelled or not allowed',
                code: PasskeyErrorCode.USER_CANCELLED
            };
        }
        if (error.name === 'SecurityError') {
            return {
                message: 'The operation is insecure',
                code: PasskeyErrorCode.SECURITY_ERROR
            };
        }
        if (error.name === 'AbortError') {
            return {
                message: 'The operation was aborted',
                code: PasskeyErrorCode.USER_CANCELLED
            };
        }
        if (error.name === 'ConstraintError') {
            return {
                message: 'A passkey already exists for this account',
                code: PasskeyErrorCode.INVALID_STATE
            };
        }
        if (error.name === 'InvalidStateError') {
            return {
                message: 'The authenticator is in an invalid state',
                code: PasskeyErrorCode.INVALID_STATE
            };
        }
        if (error.name === 'NotSupportedError') {
            return {
                message: 'Passkeys are not supported on this device',
                code: PasskeyErrorCode.NOT_SUPPORTED
            };
        }
        // Handle network errors
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
            return {
                message: 'Network timeout. Please check your connection and try again.',
                code: PasskeyErrorCode.NETWORK_ERROR
            };
        }
        // Check for timeout
        if (error.message?.includes('timeout')) {
            return {
                message: 'The operation timed out. Please try again.',
                code: PasskeyErrorCode.TIMEOUT
            };
        }
        // Default error
        return {
            message: error.message || 'Passkey operation failed',
            code: PasskeyErrorCode.UNKNOWN_ERROR
        };
    }
    /**
     * Log error conditionally based on environment
     */
    logError(context, error) {
        if (process.env.NODE_ENV === 'development') {
            console.error(`[PasskeyService] ${context}:`, error);
        }
    }
}
// Export singleton instance
export const passkeyService = PasskeyService.getInstance();
